import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface SubscriptionStatusResponse {
  tier?: string;
  status?: string;
  trialEndsAt?: string | null;
  expiresAt?: string | null;
  pricingEnabled?: boolean;
}

export interface SubscriptionData {
  tier: string;
  status: string;
  trialEndsAt: Date | null;
  expiresAt: Date | null;
  isActive: boolean;
  isTrial: boolean;
  isPastDue: boolean;
  isCanceled: boolean;
  isPaymentsEnabled: boolean;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

const SAFE_DEFAULTS = {
  tier: "free",
  status: "trialing",
  trialEndsAt: null as Date | null,
  expiresAt: null as Date | null,
  isActive: true,
  isTrial: true,
  isPastDue: false,
  isCanceled: false,
  isPaymentsEnabled: false,
};

function toDate(v: string | null | undefined): Date | null {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

function deriveFlags(raw: SubscriptionStatusResponse) {
  const tier = raw.tier ?? "free";
  const status = raw.status ?? "trialing";
  return {
    tier,
    status,
    trialEndsAt: toDate(raw.trialEndsAt),
    expiresAt: toDate(raw.expiresAt),
    isActive: status === "active" || status === "trialing",
    isTrial: status === "trialing",
    isPastDue: status === "past_due",
    isCanceled: status === "canceled",
    isPaymentsEnabled: Boolean(raw.pricingEnabled),
  };
}

export function useSubscription(): SubscriptionData {
  const query = useQuery({
    queryKey: ["subscription"],
    queryFn: async () => {
      // If there is no authenticated session, don't even call the function.
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData?.session) {
        throw new Error("UNAUTHENTICATED");
      }

      const { data, error } = await supabase.functions.invoke(
        "get-subscription-status",
      );
      if (error) throw error;
      return (data ?? {}) as SubscriptionStatusResponse;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });

  // Always return a safe, usable object — never throw to the caller.
  if (query.data) {
    const flags = deriveFlags(query.data);
    return {
      ...flags,
      isLoading: query.isLoading,
      error: null,
      refetch: () => {
        void query.refetch();
      },
    };
  }

  return {
    ...SAFE_DEFAULTS,
    isLoading: query.isLoading,
    error: query.error instanceof Error ? query.error : null,
    refetch: () => {
      void query.refetch();
    },
  };
}
